import { useState } from "react";
import { Plus, Pencil, Award, Power } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtDate } from "../../lib/api";
import { PageHeader, Badge, SkeletonGrid, ErrorState, EmptyState } from "../../components/common";
import { Field, Input, Select, STATUS_OPTS, Toolbar, Table, Row, Td, IconBtn, FormDialog, ConfirmDelete } from "../../components/admin/kit";

export default function AdminCertificates() {
  const { data, loading, error, reload } = useApi("/admin/certificates");
  const students = useApi("/admin/students");
  const courses = useApi("/admin/courses");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const body = (f) => ({ user_id: f.user_id, course_id: f.course_id, number: f.number, issue_date: f.issue_date, status: f.status });
  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.put(`/admin/certificates/${form.id}`, body(form));
      else await api.post("/admin/certificates", body(form));
      toast.success(form.id ? "Sertifika güncellendi." : "Sertifika oluşturuldu.");
      setForm(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const toggle = (c) => api.put(`/admin/certificates/${c.id}`, body({ ...c, status: c.status === "active" ? "passive" : "active" })).then(() => { toast.success("Durum güncellendi."); reload(); }).catch((e) => toast.error(errMsg(e)));
  const del = (c) => api.delete(`/admin/certificates/${c.id}`).then(() => { toast.success("Sertifika silindi."); reload(); }).catch((e) => toast.error(errMsg(e)));
  const list = (data || []).filter((c) => `${c.number} ${c.student_name} ${c.course_title}`.toLocaleLowerCase("tr-TR").includes(q.toLocaleLowerCase("tr-TR")));
  return (
    <div>
      <PageHeader eyebrow="Yönetim" title="Sertifikalar" desc="Kursiyerlere sertifika kaydı oluşturun." />
      <Toolbar q={q} setQ={setQ} placeholder="Numara, kursiyer veya eğitim ara..." action={
        <button onClick={() => setForm({ user_id: students.data?.[0]?.id || "", course_id: courses.data?.[0]?.id || "", number: "", issue_date: new Date().toISOString().slice(0, 10), status: "active" })} className="btn-primary inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-semibold" data-testid="add-certificate-btn"><Plus className="h-4 w-4" /> Yeni Sertifika</button>
      } />
      {error ? <ErrorState message={error} onRetry={reload} /> : loading ? <SkeletonGrid count={1} className="h-72" cols="" /> : list.length === 0 ? (
        <EmptyState icon={Award} title="Henüz sertifika oluşturulmadı." desc="Eğitimini tamamlayan kursiyerler için sertifika oluşturun." />
      ) : (
        <Table head={["Sertifika No", "Kursiyer", "Eğitim", "Tarih", "Durum", ""]} testid="certificates-table">
          {list.map((c) => (
            <Row key={c.id} data-testid={`certificate-row-${c.id}`}>
              <Td className="font-mono text-xs font-semibold">{c.number}</Td>
              <Td className="font-semibold text-[var(--ink)]">{c.student_name}</Td>
              <Td className="text-slate-600">{c.course_title}</Td>
              <Td className="text-xs text-slate-500">{fmtDate(c.issue_date)}</Td>
              <Td><Badge status={c.status} /></Td>
              <Td>
                <div className="flex justify-end gap-1">
                  <IconBtn icon={Pencil} label="Düzenle" onClick={() => setForm(c)} data-testid={`edit-certificate-${c.id}`} />
                  <IconBtn icon={Power} label="Aktif/Pasif" onClick={() => toggle(c)} data-testid={`toggle-certificate-${c.id}`} />
                  <ConfirmDelete onConfirm={() => del(c)} testid={`delete-certificate-${c.id}`} />
                </div>
              </Td>
            </Row>
          ))}
        </Table>
      )}
      <FormDialog open={!!form} onClose={() => setForm(null)} title={form?.id ? "Sertifikayı Düzenle" : "Yeni Sertifika"} onSubmit={save} saving={saving} testid="certificate-form-dialog">
        {form && (
          <>
            <Field label="Kursiyer"><Select value={form.user_id} onChange={set("user_id")} options={(students.data || []).map((s) => [s.id, s.name])} data-testid="certificate-student-select" /></Field>
            <Field label="Eğitim"><Select value={form.course_id} onChange={set("course_id")} options={(courses.data || []).map((c) => [c.id, c.title])} data-testid="certificate-course-select" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Sertifika No"><Input value={form.number} onChange={set("number")} placeholder="Boş bırakılırsa otomatik" data-testid="certificate-number-input" /></Field>
              <Field label="Düzenleme Tarihi"><Input type="date" value={form.issue_date} onChange={set("issue_date")} data-testid="certificate-date-input" /></Field>
            </div>
            <Field label="Durum"><Select value={form.status} onChange={set("status")} options={STATUS_OPTS} data-testid="certificate-status-select" /></Field>
          </>
        )}
      </FormDialog>
    </div>
  );
}
